import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from "lucide-react";

const SEV = { high: "text-red-600 bg-red-50 border-red-200", medium: "text-amber-700 bg-amber-50 border-amber-200", low: "text-zinc-600 bg-zinc-50 border-zinc-200" };

// REQUIRED Fable 5 adversarial VERIFIER (officer/admin). Runs on EVERY bid — no longer optional.
// VERIFY-ONLY: surfaces flags for officer review, never overrides pricing/compliance or the gate.
// "Verify now" re-runs the review on demand; the panel shows "verified by Fable 5" and any findings.
export function FableVerify({ dealId }) {
  const [state, setState] = useState({ required: true, cost_note: "", status: null, findings: null });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/bids/${dealId}/fable`).then((r) => setState(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load fable state when the deal id changes
  useEffect(() => { load(); }, [dealId]);

  // poll while a verification is running
  useEffect(() => {
    if (state.status !== "running") return;
    const t = setInterval(() => api.get(`/bids/${dealId}/fable`).then((r) => {
      setState(r.data); if (r.data.status !== "running") clearInterval(t);
    }).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [state.status, dealId]);

  const runNow = async () => {
    setBusy(true);
    try { await api.post(`/bids/${dealId}/fable-verify`); setState((s) => ({ ...s, status: "running" })); setOpen(true); toast.success("Fable 5 review started — pricing + compliance."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start review"); } finally { setBusy(false); }
  };

  const f = state.findings;
  const steps = f?.steps || {};
  const total = f?.total_findings ?? 0;
  const verifiedByFable = Object.values(steps).some((s) => s.verified_by === "claude-fable-5");
  const fellBack = Object.values(steps).some((s) => s.verified_by && s.verified_by !== "claude-fable-5");

  return (
    <div className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4 mb-5" data-testid="fable-verify">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-lg bg-zinc-900 flex items-center justify-center"><ShieldCheck className="h-4.5 w-4.5 text-gold" /></span>
          <div>
            <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              Fable 5 verify — hardest steps
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-900 text-gold font-semibold" data-testid="fable-required-chip">REQUIRED</span>
              {verifiedByFable && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid="verified-by-fable-chip">verified by Fable 5</span>}
              {fellBack && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid="fable-fellback-chip">fell back to Opus 4.8</span>}
            </div>
            <div className="text-[11px] text-zinc-500">{state.cost_note || "Required second adversarial review of pricing + compliance (runs automatically on every engine run)."}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={runNow} disabled={busy || state.status === "running"} data-testid="fable-run-now"
            className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
            {state.status === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            {state.status === "running" ? "Reviewing…" : "Re-verify now"}
          </button>
        </div>
      </div>

      {f && (
        <div className="mt-3 border-t border-zinc-100 pt-3">
          <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-sm text-zinc-800" data-testid="fable-findings-toggle">
            {total > 0 ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            <b>{total}</b> finding{total === 1 ? "" : "s"} from the last review {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {open && (
            <div className="mt-2 space-y-3" data-testid="fable-findings">
              {["pricing", "compliance"].map((k) => steps[k] && (
                <div key={k}>
                  <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide flex items-center gap-2">
                    {k} <span className="font-normal normal-case text-zinc-400">· produced by Opus 4.8 · verified by {steps[k].verified_by || "—"}</span>
                    {steps[k].error && <span className="text-amber-600 normal-case">· {steps[k].error}</span>}
                  </div>
                  {steps[k].assessment && <p className="text-xs text-zinc-600 mt-1">{steps[k].assessment}</p>}
                  {(steps[k].findings || []).map((fi, i) => (
                    <div key={i} className={`text-xs border rounded-md px-2 py-1.5 mt-1.5 ${SEV[fi.severity] || SEV.low}`} data-testid={`fable-finding-${k}-${i}`}>
                      <b className="uppercase text-[10px]">{fi.severity}</b> — {fi.issue}{fi.where ? <span className="opacity-70"> ({fi.where})</span> : ""}
                    </div>
                  ))}
                  {(steps[k].findings || []).length === 0 && <div className="text-xs text-emerald-600 mt-1">No issues flagged.</div>}
                </div>
              ))}
              <p className="text-[10px] text-zinc-400">Advisory only — these flags never change the price or the gate. Resolve them before submit.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
